import React, { useEffect, useCallback, useRef } from 'react'

// Scroll position recovery system
export const useScrollRecovery = () => {
  useEffect(() => {
    // Save scroll position every 500ms while scrolling
    let scrollTimer: NodeJS.Timeout
    
    const saveScrollPosition = () => {
      const scrollData = {
        x: window.scrollX,
        y: window.scrollY,
        timestamp: Date.now(),
        url: window.location.href
      }
      
      try {
        sessionStorage.setItem('highpoint_scroll_position', JSON.stringify(scrollData))
      } catch (e) {
        // Storage might be full, ignore
      }
    }

    const handleScroll = () => {
      clearTimeout(scrollTimer)
      scrollTimer = setTimeout(saveScrollPosition, 500)
    }

    // Restore scroll position on load
    const restoreScrollPosition = () => {
      try {
        const savedData = sessionStorage.getItem('highpoint_scroll_position')
        if (savedData) {
          const { x, y, timestamp, url } = JSON.parse(savedData)
          
          // Only restore if it's the same page and within last 5 minutes
          if (url === window.location.href && Date.now() - timestamp < 300000) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
              window.scrollTo({ top: y, left: x, behavior: 'instant' })
            })
          }
        }
      } catch (e) {
        // Invalid data, ignore
      }
    }

    // Restore position after a short delay to let React render
    const restoreTimer = setTimeout(restoreScrollPosition, 100)
    
    window.addEventListener('scroll', handleScroll, { passive: true })
    
    // Save position before page unload
    const handleBeforeUnload = () => {
      saveScrollPosition()
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      clearTimeout(scrollTimer)
      clearTimeout(restoreTimer)
    }
  }, [])
}

// Memory management and crash prevention
export const useMemoryOptimization = () => {
  const cleanupCallbacks = useRef<Array<() => void>>([])
  
  useEffect(() => {
    // Monitor memory usage
    const checkMemory = () => {
      if ('memory' in performance) {
        const memInfo = (performance as any).memory
        const usedMB = memInfo.usedJSHeapSize / (1024 * 1024)
        const limitMB = memInfo.jsHeapSizeLimit / (1024 * 1024)
        
        // If memory usage is over 80%, trigger cleanup
        if (usedMB / limitMB > 0.8) {
          console.warn('High memory usage detected, triggering cleanup')
          triggerMemoryCleanup()
        }
      }
    }

    const triggerMemoryCleanup = () => {
      // Run all registered cleanup callbacks
      cleanupCallbacks.current.forEach(cleanup => {
        try {
          cleanup()
        } catch (e) {
          console.warn('Cleanup callback failed:', e)
        }
      })
      
      // Force garbage collection if available
      if ('gc' in window) {
        (window as any).gc()
      }
    }

    // Check memory every 10 seconds
    const memoryInterval = setInterval(checkMemory, 10000)

    // Listen for memory pressure events
    const handleMemoryPressure = () => {
      console.warn('Memory pressure detected, triggering cleanup')
      triggerMemoryCleanup()
    }

    // Some browsers support memory pressure events
    if ('onmemorypressure' in window) {
      window.addEventListener('memorypressure', handleMemoryPressure)
    }

    return () => {
      clearInterval(memoryInterval)
      window.removeEventListener('memorypressure', handleMemoryPressure)
    }
  }, [])

  const registerCleanup = useCallback((cleanup: () => void) => {
    cleanupCallbacks.current.push(cleanup)
    
    // Return unregister function
    return () => {
      const index = cleanupCallbacks.current.indexOf(cleanup)
      if (index > -1) {
        cleanupCallbacks.current.splice(index, 1)
      }
    }
  }, [])

  return { registerCleanup }
}

// Crash detection and recovery
export const useCrashRecovery = () => {
  useEffect(() => {
    // Mark that the app started successfully
    const markAppStart = () => {
      sessionStorage.setItem('highpoint_app_started', Date.now().toString())
    }

    // Check if previous session crashed
    const checkForCrash = () => {
      const lastStart = sessionStorage.getItem('highpoint_app_started')
      const lastCrash = sessionStorage.getItem('highpoint_crash_detected')
      
      if (lastStart && !lastCrash) {
        const startTime = parseInt(lastStart)
        const timeSinceStart = Date.now() - startTime
        
        // If the app was marked as started but we're loading again within 5 minutes
        // without a proper cleanup, it likely crashed
        if (timeSinceStart < 300000) {
          console.warn('Potential crash detected from previous session')
          
          // Clear some potentially problematic stored data
          try {
            sessionStorage.removeItem('highpoint_heavy_data')
            localStorage.removeItem('highpoint_cache')
          } catch (e) {
            // Storage access failed
          }
        }
      }
      
      // Clear crash marker
      sessionStorage.removeItem('highpoint_crash_detected')
    }

    // Handle unhandled errors
    const handleError = (event: ErrorEvent) => {
      console.error('Unhandled error:', event.error)
      sessionStorage.setItem('highpoint_crash_detected', Date.now().toString())
      
      // Try to save current state before potential crash
      try {
        const crashData = {
          error: event.error?.message || 'Unknown error',
          url: window.location.href,
          timestamp: Date.now(),
          userAgent: navigator.userAgent
        }
        sessionStorage.setItem('highpoint_crash_info', JSON.stringify(crashData))
      } catch (e) {
        // Storage failed, ignore
      }
    }

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason)
      sessionStorage.setItem('highpoint_crash_detected', Date.now().toString())
    }

    checkForCrash()
    markAppStart()

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    // Mark clean shutdown
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('highpoint_app_started')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      sessionStorage.removeItem('highpoint_app_started')
    }
  }, [])
}

// Intersection Observer cleanup for performance
export const useIntersectionCleanup = () => {
  const observers = useRef<IntersectionObserver[]>([])
  
  const createObserver = useCallback((callback: IntersectionObserverCallback, options?: IntersectionObserverInit) => {
    const observer = new IntersectionObserver(callback, {
      rootMargin: '50px',
      threshold: 0.1,
      ...options
    })
    
    observers.current.push(observer)
    
    return {
      observer,
      cleanup: () => {
        observer.disconnect()
        const index = observers.current.indexOf(observer)
        if (index > -1) {
          observers.current.splice(index, 1)
        }
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      // Cleanup all observers on unmount
      observers.current.forEach(observer => observer.disconnect())
      observers.current = []
    }
  }, [])

  return { createObserver }
}

// Image lazy loading with memory cleanup
export const useLazyImages = () => {
  const imageCache = useRef<Set<string>>(new Set())
  
  const loadImage = useCallback((src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      if (imageCache.current.has(src)) {
        // Create new image element even if cached to avoid memory leaks
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
        return
      }

      const img = new Image()
      img.onload = () => {
        imageCache.current.add(src)
        resolve(img)
      }
      img.onerror = reject
      img.src = src
    })
  }, [])

  const clearCache = useCallback(() => {
    imageCache.current.clear()
  }, [])

  return { loadImage, clearCache }
}